// 云端 API 客户端：会话 Cookie + Bearer token 双通道
// 同源（生产 Web）走 Cookie；跨源（本地开发 / Capacitor App）走 localStorage 中的 token
import type { BirthProfile } from './bazi'
import type { BaziReading } from './baziReading'

export interface AuthUser {
  id: string
  email: string
  nickname: string
}

const API_ORIGIN = 'https://dayi-compass.pages.dev'
const TOKEN_KEY = 'dayi.sessionToken'

function apiBase(): string {
  const host = window.location.hostname
  return host === 'localhost' || host === '127.0.0.1' ? API_ORIGIN : ''
}

export function saveSessionToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

export function getSessionToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getSessionToken()
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`
  if (init.body) headers['Content-Type'] = 'application/json'
  const res = await fetch(apiBase() + path, {
    ...init,
    credentials: 'include',
    headers: { ...headers, ...(init.headers ?? {}) },
  })
  const data = (await res.json().catch(() => null)) as (T & { error?: string }) | null
  if (!res.ok) {
    const msg = data && typeof data.error === 'string' ? data.error : `HTTP ${res.status}`
    throw new Error(msg)
  }
  return data as T
}

export const api = {
  me: () => request<{ user: AuthUser }>('/api/auth/me'),
  login: (email: string, password: string) =>
    request<{ user: AuthUser; token?: string }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  register: (email: string, password: string, nickname: string) =>
    request<{ user: AuthUser; token?: string }>('/api/auth/register', { method: 'POST', body: JSON.stringify({ email, password, nickname }) }),
  logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),
  getData: () => request<{ profiles: BirthProfile[]; readings: Record<string, BaziReading> }>('/api/data'),
  push: (body: { profiles?: BirthProfile[]; readings?: Array<{ profileId: string; data: BaziReading }> }) =>
    request<{ ok: boolean }>('/api/data', { method: 'POST', body: JSON.stringify(body) }),
  deleteProfile: (id: string) =>
    request<{ ok: boolean }>(`/api/data?profile=${encodeURIComponent(id)}`, { method: 'DELETE' }),
  deleteReading: (profileId: string) =>
    request<{ ok: boolean }>(`/api/data?profileId=${encodeURIComponent(profileId)}`, { method: 'DELETE' }),
}
